import { migrateLegacyChart, legacyBelongsTo } from '@/util/chart/customChartModel';

// DB baru, bukan upgrade ChartDB: tab lama yang masih buka ChartDB v1 bakal ngeblok
// upgrade selamanya, dan kalau rilis ditarik mundur chart lamanya tetap kebaca.
const DB_NAME = 'CustomChartDB';
const DB_VERSION = 1;
const CHARTS = 'charts';
const LEGACY_DONE = 'legacyDone';
const LEGACY_DB = 'ChartDB';
const LEGACY_STORE = 'charts';

export const BLOCKED = 'blocked';

function plain(value) {
  return JSON.parse(JSON.stringify(value));
}

function read(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Tulis dianggap beres pas transaksinya commit, bukan pas request-nya sukses.
function committed(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export function openChartDb() {
  return new Promise((resolve, reject) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      reject(e);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CHARTS)) {
        db.createObjectStore(CHARTS, { keyPath: 'id' }).createIndex('companyId', 'companyId');
      }
      if (!db.objectStoreNames.contains(LEGACY_DONE)) db.createObjectStore(LEGACY_DONE, { keyPath: 'id' });
    };
    req.onblocked = () => reject(new Error(BLOCKED));
    req.onsuccess = () => {
      const db = req.result;
      // Tab lain minta upgrade: lepas koneksi biar gak ngeblok.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
}

// ChartDB lama cuma dibaca. Dibuka tanpa versi; kalau belum pernah ada, pembukaannya
// dibatalkan biar gak bikin DB kosong.
function readLegacyCharts() {
  return new Promise((resolve) => {
    let req;
    try {
      req = indexedDB.open(LEGACY_DB);
    } catch (e) {
      resolve([]);
      return;
    }
    req.onupgradeneeded = () => req.transaction.abort();
    req.onerror = () => resolve([]);
    req.onblocked = () => resolve([]);
    req.onsuccess = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(LEGACY_STORE)) {
        db.close();
        resolve([]);
        return;
      }
      const get = db.transaction([LEGACY_STORE], 'readonly').objectStore(LEGACY_STORE).getAll();
      get.onsuccess = () => {
        db.close();
        resolve(get.result || []);
      };
      get.onerror = () => {
        db.close();
        resolve([]);
      };
    };
  });
}

export async function loadCharts(db, companyId) {
  const tx = db.transaction([CHARTS, LEGACY_DONE], 'readonly');
  const [charts, doneRecords] = await Promise.all([
    read(tx.objectStore(CHARTS).getAll()),
    read(tx.objectStore(LEGACY_DONE).getAll()),
  ]);
  const migrated = [];
  if (companyId !== null && companyId !== undefined && companyId !== '') {
    const doneIds = new Set((doneRecords || []).map((r) => r.id));
    const legacy = await readLegacyCharts();
    legacy.forEach((old) => {
      try {
        if (!old || doneIds.has(old.id) || !legacyBelongsTo(old, companyId)) return;
        migrated.push(migrateLegacyChart(old, companyId));
      } catch (e) {
        console.warn('[custom chart] chart lama gagal dimigrasi', e && e.message);
      }
    });
    if (migrated.length) {
      const write = db.transaction([CHARTS, LEGACY_DONE], 'readwrite');
      migrated.forEach((chart) => {
        write.objectStore(CHARTS).put(plain(chart));
        write.objectStore(LEGACY_DONE).put({ id: chart.id });
      });
      await committed(write);
    }
  }
  return (charts || []).concat(migrated)
    .filter((chart) => String(chart.companyId) === String(companyId))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

export function saveChart(db, chart) {
  const tx = db.transaction([CHARTS], 'readwrite');
  tx.objectStore(CHARTS).put(plain(chart));
  return committed(tx);
}

export function deleteChart(db, id) {
  const tx = db.transaction([CHARTS], 'readwrite');
  tx.objectStore(CHARTS).delete(id);
  return committed(tx);
}

export function saveOrder(db, charts) {
  const tx = db.transaction([CHARTS], 'readwrite');
  const store = tx.objectStore(CHARTS);
  // Tab lain bisa udah ngedit chart yang sama; yang diubah cuma urutan.
  charts.forEach((chart, i) => {
    const req = store.get(chart.id);
    req.onsuccess = () => {
      const record = req.result;
      if (record) store.put({ ...record, order: i });
    };
  });
  return committed(tx);
}
